import { useEffect, useState } from "react";
import { Box, Card, Skeleton, Typography } from "@mui/material";
import {
  MiniMap,
  Node,
  ReactFlow,
  useNodesInitialized,
  useReactFlow,
} from "@xyflow/react";
import { useParams } from "react-router-dom";
import __ from "src/utils/Translations";
import useOrgchartEmployeesStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/store";
import Controls from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Controls/Controls";
import "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/MiniMap/_minimap.scss";
import { useQuadTreeVisibility } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/hooks/useQuadTreeVisibility";
import {
  reactFlowSettings,
  setCenterOptions,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/OrgchartTree/OrgchartSettings";
// eslint-disable-next-line
import { getAutoLayoutWorker } from "../../workers/getOrgchartWebWorker";
import { IEmployeeInOrgchartChild } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/api";
import type { Edge } from "@xyflow/react";

const AutoLayoutWorker: Worker | undefined = getAutoLayoutWorker();

const OrgchartTree = () => {
  const { nodeId: urlNodeId } = useParams() as { nodeId: string | undefined };
  const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
  const flow = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    allNodes,
    searchedNodeId,
    hierarchyData,
    wasSearchedNodeCleared,
    wasSearchTriggered,
  } = useOrgchartEmployeesStore();

  useEffect(() => {
    if (urlNodeId && !wasSearchedNodeCleared) {
      useOrgchartEmployeesStore.getState().setSearchedNodeId(urlNodeId);
    }
  }, [urlNodeId, wasSearchedNodeCleared]);

  const handleChangeFullScreenMode = () => {
    setFullScreenMode(!fullScreenMode);
  };

  useQuadTreeVisibility(nodes);

  useEffect(() => {
    if (allNodes.length > 0 && !searchedNodeId && !wasSearchTriggered) {
      AutoLayoutWorker.postMessage({ nodes: allNodes, edges });
      AutoLayoutWorker.onmessage = (event) => {
        setNodes(event.data.nodes);
        setEdges(event.data.edges);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allNodes.length, searchedNodeId, setNodes, setEdges]);

  useEffect(() => {
    const { nodeToFocusOn } = useOrgchartEmployeesStore.getState();

    if (nodeToFocusOn && searchedNodeId === null) {
      const position = flow.getNode(nodeToFocusOn.id)?.position;
      const node = flow.getNode(nodeToFocusOn.id) as Node;

      const height = node?.measured?.height;
      const width = node?.measured?.width;

      if (nodeToFocusOn.isCollapsed && position && width && height) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            position.x + width / 2,
            position.y + height / 2,
            setCenterOptions
          );
        });
      } else if (!nodeToFocusOn.isCollapsed && position && width && height) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            position.x + width / 2,
            position.y + 50 + height * 2,
            setCenterOptions
          );
        });
      }
    }
  }, [nodes, flow, searchedNodeId]);

  // Handle hierarchy-based search (search mode)
  useEffect(() => {
    if (hierarchyData && searchedNodeId) {
      const buildNodesAndEdges = (
        children: IEmployeeInOrgchartChild[],
        parentId: string
      ): { nodes: Node[]; edges: Edge[] } => {
        const nodesList: Node[] = [];
        const edgesList: Edge[] = [];

        children.forEach((child) => {
          nodesList.push({
            id: child.employeeId,
            data: {
              employeeId: child.employeeId,
              initials: child.initials,
              name: child.name,
              position: child.position,
              area: child.area,
              isSupervisor: child.isSupervisor,
              hasChildren: child.isSupervisor,
              subordinateCount: child.subordinateCount,
              parentId,
              supervisorId: parentId,
            },
            position: { x: 0, y: 0 },
            type: "employeeNode",
          });

          edgesList.push({
            id: `${parentId}-${child.employeeId}`,
            source: parentId,
            target: child.employeeId,
            type: "customEdge",
            hidden: false,
          });

          // Set collapsed status to false if node has children in hierarchy
          if (child.children?.length > 0) {
            useOrgchartEmployeesStore
              .getState()
              .setChildrenCollapsedStatus(
                new Map(
                  useOrgchartEmployeesStore.getState().childrenCollapsedStatus
                ).set(child.employeeId, false)
              );

            const nested = buildNodesAndEdges(child.children, child.employeeId);
            nodesList.push(...nested.nodes);
            edgesList.push(...nested.edges);
          }
        });

        return { nodes: nodesList, edges: edgesList };
      };

      const { nodes: childNodes, edges: childEdges } = buildNodesAndEdges(
        hierarchyData.children,
        "root"
      );

      // Deduplicate nodes (hierarchy may contain same employee in multiple paths)
      const seenNodeIds = new Set<string>();
      const uniqueChildNodes = childNodes.filter((node) => {
        if (seenNodeIds.has(node.id)) return false;
        seenNodeIds.add(node.id);
        return true;
      });
      const uniqueChildEdges = childEdges.filter((edge) => {
        const key = edge.id;
        if (seenNodeIds.has(key)) return false;
        seenNodeIds.add(key);
        return true;
      });

      const { rootSubordinatesCount } = useOrgchartEmployeesStore.getState();

      const searchedRootNode: Node = {
        id: "root",
        data: {
          avatar: null,
          name: hierarchyData.structureName,
          isSupervisor: true,
          hasChildren: true,
          subordinateCount: rootSubordinatesCount,
          parentId: null,
        },
        position: { x: 0, y: 0 },
        type: "customRootNode",
      };

      const finalNodes = [searchedRootNode, ...uniqueChildNodes];

      // Set all search nodes as visible so edges render
      const visibilityMap = new Map<string, boolean>();
      finalNodes.forEach((node) => visibilityMap.set(node.id, true));
      useOrgchartEmployeesStore.getState().setVisibleNodesRef(visibilityMap);

      AutoLayoutWorker.postMessage({
        nodes: finalNodes,
        edges: uniqueChildEdges,
      });
      AutoLayoutWorker.onmessage = (event) => {
        const { nodes: layoutNodes, edges: layoutEdges } = event.data;
        setNodes(layoutNodes);
        setEdges(layoutEdges);
      };
    }
  }, [searchedNodeId, hierarchyData, setNodes, setEdges]);

  useEffect(() => {
    if (searchedNodeId && nodesInitialized) {
      const searchedNode = flow.getNode(searchedNodeId);
      const height = searchedNode?.measured?.height;
      const width = searchedNode?.measured?.width;
      if (searchedNode && height && width) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            searchedNode.position.x + width / 2,
            searchedNode.position.y + height / 2,
            setCenterOptions
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedNodeId, nodesInitialized, nodes]);

  useEffect(() => {
    if (flow.viewportInitialized) {
      window.requestAnimationFrame(() => {
        flow.fitView({ maxZoom: 0.7 });
      });
    }
  }, [flow, fullScreenMode]);

  useEffect(() => {
    if (nodesInitialized) {
      AutoLayoutWorker.postMessage({ nodes, edges });
      AutoLayoutWorker.onmessage = (aEvent) => {
        setNodes(aEvent.data.nodes);
        setEdges(aEvent.data.edges);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesInitialized]);

  return (
    <Card
      data-testid="os__view-orgchart"
      sx={
        fullScreenMode
          ? {
              position: "fixed",
              height: "100vh",
              width: "100vw",
              left: 0,
              top: "60px",
              zIndex: 1000000,
              padding: 0,
            }
          : {
              margin: "16px 0 0 0",
              padding: "0",
              height: "641px",
              width: "100%",
              left: 0,
              top: 0,
            }
      }
    >
      <Box
        sx={{
          backgroundColor: "#F0F7FF",
          padding: "8px 16px",
          borderBottom: "1px solid #A1DEFA",
        }}
      >
        <Typography sx={{ fontWeight: "fontWeightMedium" }}>
          {__(`Struktura`)}
        </Typography>
      </Box>
      <Box
        sx={{
          height: fullScreenMode ? "calc(100vh - 101px)" : "600px",
          position: "relative",
          contentVisibility: "auto",
        }}
      >
        {(searchedNodeId && nodes.length === 0) || !nodesInitialized ? (
          <Skeleton variant="rounded" height="600px" />
        ) : null}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          {...reactFlowSettings}
        >
          <Controls setFullScreenMode={handleChangeFullScreenMode} />
          {nodes.length <= 60 && (
            <MiniMap
              pannable
              maskColor="#86868640"
              style={{ borderRadius: "5px" }}
            />
          )}
        </ReactFlow>
      </Box>
    </Card>
  );
};

export default OrgchartTree;
